import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Calendar, Youtube, MessageSquare, Globe, MapPin, Facebook, Instagram, Twitter, Linkedin, Video } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Libs/firebase-config.mjs';
import ContactItem from '../shared/ContactItem';

// Icon mapping
const ICON_MAP = {
  Phone,
  Mail,
  Calendar,
  Youtube,
  MessageSquare,
  Globe,
  MapPin,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Video
};

const ConnectSection = ({ darkMode }) => {
  const [connectData, setConnectData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConnectData();
  }, []);

  const loadConnectData = async () => {
    try {
      const docRef = doc(db, 'connectSection', 'content');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setConnectData(docSnap.data());
      } else {
        // Fallback to default data
        setConnectData({
          subtitle: 'Get In Touch',
          title: "Let's Connect",
          description: "Ministry is about building relationships and serving God's people. We welcome opportunities to connect, pray with you, or serve you in any way we can. Whether you're interested in ministry partnerships, prayer requests, or simply want to connect with our FLEC community, we'd love to hear from you.",
          image: 'https://res.cloudinary.com/dbacioerk/image/upload/v1763636346/pastors-rd1koyyyyhzzqqg7vahud891ljotap3jyzx4q8facq_ne0vb5.jpg',
          contacts: [
            { icon: 'Mail', label: 'Email', value: 'faithlifeevangelistic@gmail.com' },
            { icon: 'Youtube', label: 'YouTube', value: 'www.youtube.com/@flecchurch' },
            { icon: 'Calendar', label: 'Ministry Schedule', value: 'View FLEC events & services' }
          ],
          buttons: [
            { text: 'Ministry Inquiry', type: 'primary' },
            { text: 'Prayer Request', type: 'secondary' }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading connect section:', err);
      // Set fallback data on error
      setConnectData({
        subtitle: 'Get In Touch',
        title: "Let's Connect",
        description: "Ministry is about building relationships and serving God's people. We welcome opportunities to connect, pray with you, or serve you in any way we can. Whether you're interested in ministry partnerships, prayer requests, or simply want to connect with our FLEC community, we'd love to hear from you.",
        image: 'https://res.cloudinary.com/dbacioerk/image/upload/v1763636346/pastors-rd1koyyyyhzzqqg7vahud891ljotap3jyzx4q8facq_ne0vb5.jpg',
        contacts: [
          { icon: 'Mail', label: 'Email', value: 'faithlifeevangelistic@gmail.com' },
          { icon: 'Youtube', label: 'YouTube', value: 'www.youtube.com/@flecchurch' },
          { icon: 'Calendar', label: 'Ministry Schedule', value: 'View FLEC events & services' }
        ],
        buttons: [
          { text: 'Ministry Inquiry', type: 'primary' },
          { text: 'Prayer Request', type: 'secondary' }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <section 
        className={`py-32 px-8 ${darkMode ? 'bg-slate-800' : 'bg-white'} transition-colors duration-300`}
        style={{ minHeight: '400px', visibility: 'visible', opacity: 1 }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              Loading Connect Section...
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!connectData) {
    return null;
  }

  const data = connectData;

  return (
    <section 
      className={`py-32 px-8 ${darkMode ? 'bg-slate-800' : 'bg-white'} transition-colors duration-300`}
      style={{ 
        minHeight: '400px',
        visibility: 'visible',
        opacity: 1,
        display: 'block',
        zIndex: 1
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className={`text-sm uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-600'} font-medium mb-4 font-sans`}>
              {data.subtitle}
            </div>
            <h2 className={`text-5xl md:text-6xl font-bold mb-8 ${darkMode ? 'text-white' : 'text-slate-900'} font-sans`}>
              {data.title}
            </h2>
            
            <p className={`text-xl ${darkMode ? 'text-slate-300' : 'text-slate-600'} mb-12 leading-relaxed font-sans`}>
              {data.description}
            </p>

            {/* Dynamic Contact Items */}
            <div className="space-y-6 mb-12">
              {data.contacts && data.contacts.length > 0 ? (
                data.contacts.map((contact, index) => {
                  const IconComponent = ICON_MAP[contact.icon] || Mail;
                  return (
                    <ContactItem 
                      key={index}
                      icon={IconComponent} 
                      label={contact.label} 
                      value={contact.value} 
                      darkMode={darkMode} 
                    />
                  );
                })
              ) : (
                <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  No contact information available
                </div>
              )}
            </div>

            {/* Dynamic Buttons */}
            {data.buttons && data.buttons.length > 0 && (
              <div className="flex gap-4 flex-wrap">
                {data.buttons.map((button, index) => (
                  <button 
                    key={index}
                    className={`px-8 py-4 rounded-lg font-medium transition-all shadow-lg font-sans ${
                      button.type === 'primary'
                        ? darkMode 
                          ? 'bg-white text-slate-900 hover:bg-slate-100' 
                          : 'bg-slate-900 text-white hover:bg-slate-800'
                        : darkMode 
                          ? 'border-2 border-white text-white hover:bg-white hover:text-slate-900' 
                          : 'border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white'
                    }`}
                  >
                    {button.text}
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={data.image} 
                alt="Connect with ministry"
                className="w-full h-[600px] object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ConnectSection;